import React, { useEffect, useState } from "react";
import "./LibraryModal.css";
import InputBox from "../../../../components/reusable-input/InputBox";
import Select from "../../../../components/reusable-select/Select";
import Button from "../../../../components/reusable-button/Button";
import Switch from "../../../../components/reusable-switch/Switch";

export interface LibraryFormData {
  id?: string;
  name: string;
  authType: string; // ID
  active: boolean;
  hostname?: string;
  port?: string;
  sipServer?: string;
  sipPort?: string;
  institutionId?: string;
  sipUsername?: string;
  sipPassword?: string;
  contactEmail?: string;
  connectionType?: string;
}

interface LibraryModalProps {
  isOpen: boolean;
  mode: "add" | "edit";
  onClose: () => void;
  onSave: (data: LibraryFormData) => void;
  initialData?: LibraryFormData;
  authOptions?: { label: string; value: string }[];
}

const LibraryModal: React.FC<LibraryModalProps> = ({
  isOpen,
  mode,
  onClose,
  onSave,
  initialData,
  authOptions,
}) => {
  const [name, setName] = useState("");
  const [authType, setAuthType] = useState(""); // ID
  const [active, setActive] = useState(true);

  // PATRON API
  const [hostName, setHostName] = useState("");
  const [port, setPort] = useState("");

  // SIP 2
  const [sipServer, setSipServer] = useState("");
  const [sipPort, setSipPort] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [sipUsername, setSipUsername] = useState("");
  const [sipPassword, setSipPassword] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [showPassword, setShowPassword] = useState(true);
  const [connectionType, setConnectionType] = useState("");

  const selectedAuthLabel = authOptions
    ?.find((opt) => opt.value.toLowerCase() === authType.toLowerCase())
    ?.label.trim()
    .toLowerCase();

  // Populate fields when editing
  useEffect(() => {
    if (initialData && authOptions?.length) {
      setName(initialData.name);
      setActive(initialData.active);
      setContactEmail(initialData.contactEmail || "");
      setAuthType(initialData.authType || "");

      const label = authOptions
        .find((opt) => opt.value === initialData.authType)
        ?.label.toLowerCase();
      if (label?.toLocaleLowerCase() === "patron api") {
        setHostName(initialData.hostname || "");
        setPort(initialData.port || "");
      } else if (label?.toLocaleLowerCase() === "sip2") {
        setSipServer(initialData.sipServer || "");
        setSipPort(initialData.sipPort || "");
        setInstitutionId(initialData.institutionId || "");
        setSipUsername(initialData.sipUsername || "");
        setSipPassword(initialData.sipPassword || "");
        setConnectionType(initialData?.connectionType || "");
      }
    }
  }, [initialData, authOptions]);

  // Clear form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setName("");
      setAuthType("");
      setActive(true);
      setHostName("");
      setPort("");
      setSipServer("");
      setSipPort("");
      setInstitutionId("");
      setSipUsername("");
      setSipPassword("");
      setContactEmail("");
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const baseData: LibraryFormData = {
      id: initialData?.id,
      name,
      authType,
      active,
      contactEmail,
    };
    if (!name.trim()) {
      alert("Library Name is required");
      return;
    }
    if (!contactEmail.trim()) {
      alert("Contact Email is required");
      return;
    }
    if (!authType) {
      alert("Library Authentication Type is required");
      return;
    }
    if (selectedAuthLabel?.includes("patron api")) {
      baseData.hostname = hostName;
      baseData.port = port;
    } else if (selectedAuthLabel?.includes("sip2")) {
      baseData.sipServer = sipServer;
      baseData.sipPort = sipPort;
      baseData.institutionId = institutionId;
      baseData.sipUsername = sipUsername;
      baseData.sipPassword = sipPassword;
      baseData.connectionType = connectionType;
    }

    onSave(baseData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="library-modal" onClick={(e) => e.stopPropagation()}>
        <form className="library-modal-content" onSubmit={handleSubmit}>
          <div className="modal-title">
            {mode === "add" ? "Add Library" : "Edit Library"}
          </div>

          <div className="modal-body">
            <InputBox
              label="Library Name"
              required
              placeholder="Enter Library Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
            <InputBox
              label="Contact Email"
              required
              placeholder="Enter Contact Email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              maxLength={120}
            />

            <div style={{ marginTop: "10px" }}>
              <Select
                label="Library Authentication Type"
                name="library-type"
                required
                options={authOptions || []}
                value={authType}
                onChange={(e) => setAuthType(e.target.value)}
              />
            </div>

            {selectedAuthLabel?.toLocaleLowerCase() === "patron api" && (
              <>
                <InputBox
                  label="Host Name"
                  required
                  placeholder="Enter Host Name"
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  maxLength={50}
                />
                <InputBox
                  label="Port"
                  required
                  placeholder="Enter Port"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  maxLength={6}
                  type="number"
                />
              </>
            )}

            {selectedAuthLabel?.toLocaleLowerCase() === "sip2" && (
              <>
                <Select
                  label="Connection Type"
                  name="connection-type"
                  required
                  options={[
                    { label: "TLS", value: "tls" },
                    { label: "TCP", value: "tcp" },
                  ]}
                  value={connectionType}
                  onChange={(e) => setConnectionType(e.target.value)}
                />
                <InputBox
                  label="SIP Server"
                  required
                  placeholder="Enter SIP Server"
                  value={sipServer}
                  onChange={(e) => setSipServer(e.target.value)}
                  maxLength={50}
                />
                <InputBox
                  label="SIP Port"
                  required
                  placeholder="Enter SIP Port"
                  value={sipPort}
                  onChange={(e) => setSipPort(e.target.value)}
                  maxLength={6}
                  type="number"
                />
                <InputBox
                  label="Institution ID"
                  required
                  placeholder="Enter Institution ID"
                  value={institutionId}
                  onChange={(e) => setInstitutionId(e.target.value)}
                  maxLength={50}
                />
                <InputBox
                  label="SIP Username"
                  placeholder="Enter SIP Username"
                  value={sipUsername}
                  onChange={(e) => setSipUsername(e.target.value)}
                  maxLength={50}
                />
                <InputBox
                  label="SIP Password"
                  placeholder="Enter SIP Password"
                  type="password"
                  showPasswordToggle={true}
                  showPassword={showPassword}
                  onTogglePassword={() =>
                    setShowPassword((showPassword) => !showPassword)
                  }
                  value={sipPassword}
                  onChange={(e) => setSipPassword(e.target.value)}
                  maxLength={50}
                />
              </>
            )}

            {initialData && (
              <div style={{ marginTop: "10px" }}>
                <Switch
                  label={active ? "Active" : "Inactive"}
                  checked={active}
                  onChange={() => setActive((prev) => !prev)}
                />
              </div>
            )}
          </div>

          <div className="footer">
            <Button
              variant="outlined"
              label="Cancel"
              type="button"
              onClick={onClose}
            />
            <Button
              label={mode === "add" ? "Add Library" : "Save Changes"}
              type="submit"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default LibraryModal;
